/**
 * Модалка «Добавить в структуру» (F2-17): на странице «Охрана труда» (`/ot`, только клиент,
 * design D4 — сама страница ОТ без inline-CRUD/«Заказать») даёт единую точку входа для способов
 * завести данные в структуру — БЕЗ дублирования полей/валидации, только встраивание готовых форм
 * в вкладки общего Dialog/Tabs (ДС, F2-04):
 *  - «Импорт структуры» — `StructureImport` (Excel, p2-excel-import, structure-import.tsx);
 *  - «Добавить рабочее место» — `AddWorkplaceForm`, единая форма §7 (p3-structure-sout-training
 *    волна 7): объединяет бывшие вкладки «Добавить сотрудника» и «Рабочее место без сотрудника»
 *    (§7.2 ТЗ — «объединить варианты добавления сотрудника и рабочего места без сотрудника в один
 *    вариант "Добавить рабочее место"»).
 *
 * `CreateEmployeeForm` (§10.1) нужна ТОЛЬКО для шага «Комиссия» заказа СОУТ (`sout-wizard.tsx`,
 * `initialTab="employee"`), которого §7.2 явно не касается («Формы заказов услуг сохранить текущее
 * поведение» — не затрагиваются). D (code-review волны 7, HIGH, вариант «б»): `initialTab==='employee'`
 * рендерит ОТДЕЛЬНУЮ ветку — `Dialog` с ОДНОЙ формой сотрудника, БЕЗ `Tabs`/`TabsList` вовсе, а не
 * скрытый (`hidden`) `TabsTrigger` внутри общего `Tabs`, как было раньше. Прежний подход был хрупким:
 * ревью проследило по исходникам `@radix-ui/react-tabs`/`react-roving-focus`, что Dialog-автофокус
 * первого элемента `TabsList` при `activationMode="automatic"` МОЖЕТ (в реальном браузере, не только
 * в отсутствующем в jsdom пути) сфокусировать видимый триггер «Импорт» и молча переключить `value` —
 * тот самый баг «модалка соскакивает на другую вкладку», только другим триггером; зелёный jsdom-тест
 * это не гарантировал (cepочка фокуса там не воспроизводится). Убирая `Tabs` из этой ветки целиком,
 * убираем весь класс таких багов, а не конкретный найденный путь.
 *
 * Owner-scoping — как везде в CompanyScopeApi: модалка сама client_id не передаёт, скоуп задаёт
 * `api` (у клиента — свой, `companyApi`).
 *
 * После успеха ЛЮБОЙ формы — закрываем модалку и зовём `onSuccess` (родитель, `OtOverviewPage`,
 * перезагружает таблицу `companyApi.otOverview()`); сама модалка состояние таблицы не хранит.
 *
 * `onSuccess` получает `AddToStructureSuccessInfo` (какой источник + для «workplace» — само
 * созданное место, теперь возможно с `employee`/`shiftPartner`), чтобы `OtOverviewPage` мог
 * показать уведомление ТОЛЬКО для места и привязать его к месту — у импорта есть собственный
 * результат, дублировать под него уведомление не нужно.
 */
import { useEffect, useState } from 'react'
import { type CompanyScopeApi, type Department, type Employee, type Workplace } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/design-system/components/primitives/tabs'
import { Alert } from '@/design-system/components/feedback/alert'
import { StructureImport } from './structure-import'
import { CreateEmployeeForm } from './employees-manager'
import { AddWorkplaceForm } from './add-workplace-form'

export type AddTab = 'import' | 'employee' | 'workplace'

/**
 * Результат успеха — `tab` различает источник, `workplace` заполнен только для `tab === 'workplace'`
 * (созданное место, возможно с `employee`/`shiftPartner`, — нужно для уведомления на /ot, замечание
 * РП №4); `employee` — только для ветки «только сотрудник» (`initialTab="employee"`, шаг «Комиссия»
 * заказа СОУТ, форма заказа не затронута волной 7).
 */
export type AddToStructureSuccessInfo =
  | { tab: 'import' }
  | { tab: 'employee'; employee: Employee }
  | { tab: 'workplace'; workplace: Workplace }

/**
 * Справочники для вкладки «Добавить рабочее место» (`AddWorkplaceForm` — существующие сотрудники,
 * выбор основного и сменщика, §7.1) и для ветки «Добавить сотрудника» (`CreateEmployeeForm`, §10.1,
 * только `initialTab="employee"`). Грузим один раз при монтировании модалки — тем же паттерном
 * Promise.all, что и StructureManager/EmployeesManager.
 *
 * Осознанный tradeoff: eager-загрузка на открытие модалки (3 owner-scoped запроса), даже если
 * пользователь пришёл ради вкладки «Импорт» — зато форма рабочего места открывается мгновенно,
 * без своего спиннера. Списки лёгкие; лениться на первый переход на вкладку не стали намеренно.
 */
function useEmployeeTabData(api: CompanyScopeApi) {
  const [workplaces, setWorkplaces] = useState<Workplace[]>([])
  const [employees, setEmployees] = useState<Employee[]>([])
  const [departments, setDepartments] = useState<Department[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    Promise.all([
      api.listWorkplaces(controller.signal),
      api.listEmployees(undefined, controller.signal),
      api.listDepartments(controller.signal),
    ])
      .then(([wps, emps, deps]) => {
        if (controller.signal.aborted) return
        setWorkplaces(wps.items)
        setEmployees(emps.items)
        setDepartments(deps.items)
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setError(getErrorMessage(err, 'Не удалось загрузить справочники'))
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api])

  return { workplaces, employees, departments, loading, error }
}

export interface AddToStructureModalProps {
  api: CompanyScopeApi
  /** Закрытие БЕЗ побочных эффектов — Escape/клик вне панели/кнопка «Отмена». */
  onClose: () => void
  /** Успешное действие в ЛЮБОЙ из вкладок — родитель перезагружает таблицу ОТ. */
  onSuccess: (info: AddToStructureSuccessInfo) => void
  /** Вкладка при открытии (по умолчанию «Импорт»); шаг «Комиссия» открывает сразу «employee» (D3). */
  initialTab?: AddTab
}

export function AddToStructureModal({ api, onClose, onSuccess, initialTab = 'import' }: AddToStructureModalProps) {
  // Ветка «только сотрудник» (см. докстринг файла) не использует Tabs вовсе — `tab`-стейт ей не
  // нужен, поэтому initialTab='employee' не заводится в это состояние (не участвует в Tabs.value).
  const [tab, setTab] = useState<Exclude<AddTab, 'employee'>>(
    initialTab === 'employee' ? 'import' : initialTab,
  )
  const { workplaces, employees, departments, loading, error } = useEmployeeTabData(api)
  const employeeOnly = initialTab === 'employee'

  function handleSuccess(info: AddToStructureSuccessInfo) {
    onSuccess(info)
    onClose()
  }

  return (
    // Отмена не имеет побочных эффектов — Escape/клик вне панели безопасно закрывают модалку.
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent title="Добавить в структуру" className="max-w-3xl">
        {/* Общий видимый «Закрыть» в chrome модалки — единообразно для ОБЕИХ веток (F2-17): ни
            встроенной форме сотрудника, ни `AddWorkplaceForm` свою «Отмену» не передаём (onCancel не
            задан), чтобы закрытие не различалось. Дублирует Escape/клик-вне из общего Dialog. */}
        <Card
          header="Добавить в структуру"
          footer={
            <div className="flex justify-end">
              <Button variant="secondary" onClick={onClose}>
                Закрыть
              </Button>
            </div>
          }
        >
          {employeeOnly ? (
            // Шаг «Комиссия» заказа СОУТ (sout-wizard.tsx, initialTab="employee") — ОДНА форма
            // сотрудника, без Tabs (см. докстринг файла — почему не скрытый TabsTrigger).
            error ? (
              <Alert variant="danger">{error}</Alert>
            ) : loading ? (
              <p className="py-6 text-center text-txt-secondary">Загрузка…</p>
            ) : (
              <CreateEmployeeForm
                api={api}
                workplaces={workplaces}
                employees={employees}
                departments={departments}
                onCreated={(employee) => handleSuccess({ tab: 'employee', employee })}
              />
            )
          ) : (
            <Tabs value={tab} onValueChange={(v) => setTab(v as Exclude<AddTab, 'employee'>)}>
              <TabsList aria-label="Способ добавления в структуру" className="flex-wrap">
                <TabsTrigger value="import">Импорт структуры</TabsTrigger>
                <TabsTrigger value="workplace">Добавить рабочее место</TabsTrigger>
              </TabsList>

              <TabsContent value="import">
                <StructureImport api={api} onImported={() => handleSuccess({ tab: 'import' })} />
              </TabsContent>

              <TabsContent value="workplace">
                {error ? (
                  <Alert variant="danger">{error}</Alert>
                ) : loading ? (
                  <p className="py-6 text-center text-txt-secondary">Загрузка…</p>
                ) : (
                  <AddWorkplaceForm
                    api={api}
                    departments={departments}
                    employees={employees}
                    onCreated={(workplace) => handleSuccess({ tab: 'workplace', workplace })}
                    className="mt-0"
                  />
                )}
              </TabsContent>
            </Tabs>
          )}
        </Card>
      </DialogContent>
    </Dialog>
  )
}
